<template>
  <DxDataGrid v-bind="gridProperties" :data-source="gridSource" ref="grid"
    @row-inserted="onRowInserted"
    @row-updated="onRowUpdated"
    @row-removed="onRowRemoved">
    <DxColumnFixing :enabled="true" />
    <DxColumn data-field="Importe" :caption="$t('ofertas.grid.importe')" data-type="number"  
      cell-template="importeTemplate">
        <DxRequiredRule />
        <DxRangeRule :min="0" :message="$t('comisiones.grid.importe_validacion')" :reevaluate="true"/>
    </DxColumn>
    <template #importeTemplate="{ data }">
      <span>{{ $n(data.value) }} €</span>
    </template>
  </DxDataGrid>

</template>

<script setup lang="ts">
import { DxDataGrid, DxColumn, DxColumnFixing, DxRequiredRule, DxRangeRule, } from "devextreme-vue/data-grid";
import { getDefaultGridProperties, } from "@helpers/grid-helper";
import { onMounted, ref } from 'vue';
import type { ComisionEnergiaServicioModel, EditComisionEnergiaServicioModel } from "@/.shared/.api";
import { useApi } from "@/.shared/.api/integration";
import { useIntl } from "@/.shared/services/i18n";
import { toastError } from "@/.shared/services/notification-service";

const gridProperties = ref<DxDataGrid>();
const gridSource = ref<ComisionEnergiaServicioModel[]>();
const grid = ref<DxDataGrid>();
const api = useApi();
const intl = useIntl();

const props = defineProps({
  idServicio: {
    type: String,
    required: true
  },
  idMarco: {
    type: String,
    required: true
  },
  isEditMode: {
    type: Boolean,
    required: true
  }
});

onMounted(async () => {
  gridProperties.value = {...getDefaultGridProperties(), 
    ...{ editing: { mode: "row", 
        allowUpdating: true, 
        allowAdding: true, 
        allowDeleting: true } }};

  if (props.isEditMode) {
    var apiFunction = await api.toStandardApiFunction("getComisionesPorMarcoByIdServicio", { showLoading: false });
    gridSource.value = await apiFunction(props.idServicio, props.idMarco);
  } else {
    gridSource.value = [];
  }
});

async function onRowInserted(e: { data: ComisionEnergiaServicioModel }) {
  if (!props.isEditMode) return;
  
  try {
    var result = await api.callStandardApi("createComisionesEnergiaServicio", { showLoading: false }, props.idServicio, props.idMarco, [e.data]);

    // Actualizar directamente el Id en la fila del grid
    e.data.Id = result[0];
  } catch (error) {
    toastError(intl.i18n.t('comisiones.grid.creacion_fallo'))
  }
}

async function onRowUpdated(e: { data: ComisionEnergiaServicioModel }) {
  if (!props.isEditMode) return;
  
  try {
    if (e.data.Id) {
      await api.callStandardApi("editComisionEnergiaServicio", { showLoading: false }, e.data as EditComisionEnergiaServicioModel);
    }
  } catch (error) {
    toastError(intl.i18n.t('comisiones.grid.edicion_fallo'))
  }
}

async function onRowRemoved(e: { data: ComisionEnergiaServicioModel }) {
  if (!props.isEditMode) return;
  
  try {
    await api.callStandardApi("deleteComisionEnergiaServicio", { showLoading: false }, e.data.Id!);
  } catch (error) {
    toastError(intl.i18n.t('comisiones.grid.eliminacion_fallo'))
  }
}

defineExpose({
  getComisiones: () => {
    return gridSource.value;
  },
  idMarco: props.idMarco
});
</script>